import { FC } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { TPgFeeMatrix, TPgPaymentMethodType } from 'services/api/paymentGateway/types';
import { PG_METHOD_TYPE_LABELS } from '../constants';
import { formatDateTime, formatPercent, percentInputToFraction } from '../utils';
import { cellKey, TFeeCellDraft, TFeeDrafts, validateCell } from './draft';

type TFeeMatrixTableProps = {
	matrix: TPgFeeMatrix;
	drafts: TFeeDrafts;
	disabled: boolean;
	onChange: (key: string, patch: Partial<TFeeCellDraft>) => void;
};

type TRow = { serviceId: number | null; label: string; code: string | null; isActive: boolean };

const inheritedText = (defaultCell: TFeeCellDraft | undefined): string => {
	if (!defaultCell?.enabled || !defaultCell.isActive) return 'No fee';
	const percent = percentInputToFraction(defaultCell.percent);
	const fixed = defaultCell.fixedAmount.trim() ? ` + ${defaultCell.fixedAmount} EUR` : '';
	return `Default: ${formatPercent(percent)}${fixed}`;
};

/**
 * Fee matrix (decision 10): rows — "Default" and the services of the legal entity, columns — payment
 * methods. A cell holds a gross-up percent (typed in %, stored as a fraction) and a fixed EUR part.
 * A service without its own rule uses the default rule of the method.
 */
export const FeeMatrixTable: FC<TFeeMatrixTableProps> = ({
	matrix,
	drafts,
	disabled,
	onChange,
}) => {
	const rows: TRow[] = [
		{ serviceId: null, label: 'Default', code: null, isActive: true },
		...matrix.services.map((service) => ({
			serviceId: service.serviceId,
			label: service.serviceName,
			code: service.serviceCode,
			isActive: service.serviceIsActive,
		})),
	];

	const renderCell = (row: TRow, methodType: TPgPaymentMethodType) => {
		const key = cellKey(row.serviceId, methodType);
		const cell = drafts[key];
		if (!cell) return null;
		const isDefaultRow = row.serviceId === null;

		if (!cell.enabled) {
			const defaultCell = drafts[cellKey(null, methodType)];
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
					<Typography variant="body2" color="text.secondary">
						{isDefaultRow ? 'No fee' : inheritedText(defaultCell)}
						{cell.rule ? ' (rule will be removed)' : ''}
					</Typography>
					<Button
						size="small"
						variant="outlined"
						disabled={disabled}
						onClick={() =>
							onChange(key, {
								enabled: true,
								// A new override starts from the default values of the method.
								percent:
									cell.percent ||
									(!isDefaultRow && defaultCell?.enabled ? defaultCell.percent : '0'),
								fixedAmount:
									cell.fixedAmount ||
									(!isDefaultRow && defaultCell?.enabled ? defaultCell.fixedAmount : '0'),
							})
						}
					>
						{cell.rule ? 'Keep rule' : isDefaultRow ? 'Set fee' : 'Override'}
					</Button>
				</Box>
			);
		}

		const error = validateCell(cell);
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 150 }}>
				<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
					<TextField
						size="small"
						value={cell.percent}
						onChange={(event) => onChange(key, { percent: event.target.value })}
						disabled={disabled}
						error={!!error}
						inputProps={{ inputMode: 'decimal', 'aria-label': 'Percent' }}
						InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
						sx={{ width: 100 }}
					/>
					<IconButton
						size="small"
						title={isDefaultRow ? 'Remove the default rule' : 'Remove the override'}
						disabled={disabled}
						onClick={() => onChange(key, { enabled: false })}
					>
						<Close fontSize="small" />
					</IconButton>
				</Box>
				<TextField
					size="small"
					value={cell.fixedAmount}
					onChange={(event) => onChange(key, { fixedAmount: event.target.value })}
					disabled={disabled}
					error={!!error}
					placeholder="0"
					inputProps={{ inputMode: 'decimal', 'aria-label': 'Fixed part, EUR' }}
					InputProps={{
						startAdornment: <InputAdornment position="start">+</InputAdornment>,
						endAdornment: <InputAdornment position="end">EUR</InputAdornment>,
					}}
					sx={{ width: 140 }}
				/>
				<FormControlLabel
					sx={{ m: 0 }}
					control={
						<Checkbox
							size="small"
							checked={cell.isActive}
							disabled={disabled}
							onChange={(event) => onChange(key, { isActive: event.target.checked })}
							sx={{ p: '2px' }}
						/>
					}
					label={<Typography variant="caption">Active</Typography>}
				/>
				{error && (
					<Typography variant="caption" color="error">
						{error}
					</Typography>
				)}
				{cell.rule && (
					<Typography
						variant="caption"
						color="text.secondary"
						title={cell.rule.note ?? undefined}
						sx={{ lineHeight: 1.2 }}
					>
						#{cell.rule.ruleId}, {formatDateTime(cell.rule.updatedAt)}
						{cell.rule.updatedBy ? ` · ${cell.rule.updatedBy}` : ''}
					</Typography>
				)}
			</Box>
		);
	};

	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Service</TableCell>
						{matrix.methodTypes.map((methodType) => (
							<TableCell key={methodType} sx={{ fontWeight: 'bold' }}>
								{PG_METHOD_TYPE_LABELS[methodType] ?? methodType}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<TableRow
							key={row.serviceId ?? 'default'}
							sx={row.serviceId === null ? { backgroundColor: 'action.hover' } : undefined}
						>
							<TableCell sx={{ verticalAlign: 'top' }}>
								<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
									{row.label}
								</Typography>
								{row.code && (
									<Typography variant="caption" color="text.secondary" component="div">
										{row.code}
									</Typography>
								)}
								{row.serviceId === null && (
									<Typography variant="caption" color="text.secondary" component="div">
										Used by services without their own rule
									</Typography>
								)}
								{!row.isActive && <Chip size="small" label="Inactive service" sx={{ mt: 0.5 }} />}
							</TableCell>
							{matrix.methodTypes.map((methodType) => (
								<TableCell key={methodType} sx={{ verticalAlign: 'top' }}>
									{renderCell(row, methodType)}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
